import { useCallback, type FocusEvent } from 'react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@monksflow/monks-ui';
import { PencilIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useEditableName } from '@/shared/hooks/useEditableName';
import { useIsTruncated } from '@/shared/hooks/useIsTruncated/useIsTruncated';
import type { PageHeaderEditableNameProps } from './PageHeader.types';

/**
 * The name and the field that replaces it share one box, and the field sizes to its text, so
 * opening it moves nothing. The negative margin hands the padding back, so at rest the crumb sits
 * as close to its separator as a plain one does and only the hover fill reaches into the gap.
 */
const BOX_CLASSES = 'type-body-md-strong -mx-1.5 min-w-0 rounded-sm border px-1.5 py-0.5';

/** The character-count width a browser without `field-sizing` falls back to. */
const FALLBACK_MIN_CHARS = 8;
const FALLBACK_MAX_CHARS = 48;

/**
 * Trailing breadcrumb crumb whose name can be renamed in place: one click on the name opens the
 * field. The renaming itself is `useEditableName`.
 *
 * The name is the button's label and the tooltip names the action, so a screen reader announces
 * the current page rather than "Rename". A truncated name also puts its full text in the tooltip,
 * since the crumb is otherwise the only place it is written.
 */
export function PageHeaderEditableName(props: PageHeaderEditableNameProps) {
  const { t } = useTranslation('common');
  const name = useEditableName(props);
  const { setNode, isTruncated } = useIsTruncated();
  const { editControlRef } = name;
  const nameRef = useCallback(
    (node: HTMLButtonElement | null) => {
      editControlRef.current = node;
      setNode(node);
    },
    [editControlRef, setNode],
  );

  if (name.isEditing) {
    return (
      <input
        {...name.inputProps}
        // An unnamed page opens the field empty, not seeded with the label, and keeps the label
        // as the hint, which `field-sizing` also sizes the empty field to.
        placeholder={props.label}
        size={Math.min(
          Math.max(name.inputProps.value.length, FALLBACK_MIN_CHARS),
          FALLBACK_MAX_CHARS,
        )}
        data-testid="page-header-editable-name-input"
        className={`${BOX_CLASSES} border-border-strong bg-card text-foreground outline-none field-sizing-content`}
      />
    );
  }

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger
          asChild
          // Only the hook handing focus back as the field closes stays quiet: a "Rename"
          // hint over the name just saved reads as a failed save. Any other focus opens it,
          // the first Tab on a fresh page included, though it too has no `relatedTarget`.
          onFocus={(event: FocusEvent) => {
            if (name.isReturningFocus()) event.preventDefault();
          }}
        >
          <button
            ref={nameRef}
            type="button"
            // Matches the `aria-current` the non-editable trailing crumb gets from
            // the design system, so the two spell the current page the same way.
            aria-current="page"
            onClick={name.start}
            data-testid="page-header-editable-name"
            className={`${BOX_CLASSES} block cursor-pointer truncate border-transparent text-foreground outline-none transition-[background-color] duration-fast ease-out hover:bg-ghost-hover focus-visible:focus-ring`}
          >
            {/* With no name yet the crumb is only its padding, transparent at rest, so there
                is nothing to find and click. The label stands in, muted. */}
            {props.value || <span className="text-muted-foreground">{props.label}</span>}
          </button>
        </TooltipTrigger>
        <TooltipContent side="bottom" className="flex flex-col items-start gap-3xs">
          {isTruncated ? <span>{props.value}</span> : null}
          <span className="flex items-center gap-2xs">
            <PencilIcon className="size-3" aria-hidden />
            {t('components.pageHeader.rename')}
          </span>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
